import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  Crown,
  Check,
  X,
  Lock,
  Bot,
  Zap,
  Sparkles,
  MessageCircle,
  Video,
  Clock,
  Users,
  Plane,
  ExternalLink,
  Star,
  ArrowRight,
  Shield,
  DollarSign,
  TrendingUp
} from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";

// Feature definitions by plan level
const PLAN_FEATURES = {
  prueba: {
    name: "Plan Prueba",
    tagline: "Explora todas las funciones",
    color: "purple",
    icon: Sparkles,
    included: [
      { icon: Check, name: "Vista previa del dashboard", desc: "Explora la interfaz" },
      { icon: Check, name: "Datos de ejemplo", desc: "Ve cómo funciona" },
      { icon: Check, name: "Navegación completa", desc: "Conoce todas las secciones" },
    ],
    locked: [
      { icon: MessageCircle, name: "Subir documentos reales", upgradeTo: "estandar" },
      { icon: Bot, name: "Asistente IA personalizado", upgradeTo: "estandar" },
      { icon: MessageCircle, name: "Chat con Asesor Experto", upgradeTo: "premium" },
      { icon: Bot, name: "Simulador de Entrevista", upgradeTo: "premium" },
      { icon: Video, name: "Videollamada con Oficial", upgradeTo: "vip" },
    ]
  },
  estandar: {
    name: "Estándar",
    tagline: "Autoservicio con IA",
    color: "slate",
    icon: Shield,
    included: [
      { icon: MessageCircle, name: "WhatsApp con Servicio al Cliente", desc: "Soporte básico" },
      { icon: Bot, name: "Asistente IA 24/7", desc: "Ayuda automatizada" },
      { icon: Check, name: "Portal de documentos", desc: "Sube y organiza" },
      { icon: Check, name: "Banco de preguntas", desc: "Prepara tu entrevista" },
    ],
    locked: [
      { icon: MessageCircle, name: "Chat con Asesor Experto", upgradeTo: "premium" },
      { icon: Bot, name: "Simulador de Entrevista", upgradeTo: "premium" },
      { icon: Zap, name: "Optimización de Cita", upgradeTo: "premium" },
      { icon: Video, name: "Videollamada con Oficial", upgradeTo: "vip" },
    ]
  },
  premium: {
    name: "Premium",
    tagline: "Acompañamiento Humano + IA",
    color: "emerald",
    icon: Star,
    included: [
      { icon: MessageCircle, name: "WhatsApp con Servicio al Cliente", desc: "Soporte básico" },
      { icon: Bot, name: "Asistente IA 24/7", desc: "Ayuda automatizada" },
      { icon: Check, name: "Portal de documentos", desc: "Sube y organiza" },
      { icon: MessageCircle, name: "Chat con Asesor Experto", desc: "Respuesta 2-3 días" },
      { icon: Bot, name: "Simulador de Entrevista", desc: "Practica con IA" },
      { icon: Zap, name: "Optimización de Cita", desc: "Mejora tu probabilidad" },
    ],
    locked: [
      { icon: Video, name: "Videollamada Zoom Directa", upgradeTo: "vip" },
      { icon: Users, name: "Oficial Dedicado", upgradeTo: "vip" },
      { icon: Clock, name: "Atención Express (< 24h)", upgradeTo: "vip" },
    ]
  },
  vip: {
    name: "VIP Enterprise",
    tagline: "Adelanto de Cita Consular Incluido",
    color: "amber",
    icon: Crown,
    included: [
      { icon: Zap, name: "Adelanto de Cita Consular INCLUIDA", desc: "Promedio 300 días de adelanto" },
      { icon: Users, name: "BONUS: 3 días Orlando + 3 días Miami", desc: "PAQUETE PARA UNA PAREJA" },
      { icon: DollarSign, name: "BONO ÚNICO: $500 USD para hoteles", desc: "+3 millones de propiedades — Solo quedan 3 cupos" },
      { icon: MessageCircle, name: "WhatsApp con Servicio al Cliente", desc: "Soporte prioritario" },
      { icon: Bot, name: "Asistente IA 24/7", desc: "Ayuda automatizada" },
      { icon: Check, name: "Portal de documentos", desc: "Sube y organiza" },
      { icon: MessageCircle, name: "Chat con Asesor Experto", desc: "Respuesta prioritaria" },
      { icon: Bot, name: "Simulador de Entrevista", desc: "Practica con IA" },
      { icon: Video, name: "Videollamada Zoom Directa", desc: "Agendar cuando quieras" },
      { icon: Users, name: "Oficial Dedicado", desc: "Tu experto personal" },
      { icon: Clock, name: "Atención Lun-Sáb 9am-6pm", desc: "Respuesta < 24 horas" },
    ],
    locked: []
  }
};

const UPGRADE_BENEFITS = {
  premium: {
    title: "¿Por qué Premium?",
    subtitle: "Acompañamiento humano + IA",
    benefits: [
      "Chat directo con asesores expertos",
      "Simulador de entrevista con IA",
      "Optimización de probabilidad de cita",
      "Respuesta en 2-3 días hábiles"
    ]
  },
  vip: {
    title: "¿Por qué VIP Enterprise?",
    subtitle: "Adelanto de cita consular incluido",
    benefits: [
      "Adelanto de cita consular INCLUIDA (promedio 300 días calendario)",
      "BONUS: 3 días Orlando + 3 días Miami — PAQUETE PARA UNA PAREJA",
      "Oficial migratorio dedicado",
      "Videollamada Zoom cuando la necesites",
      "Atención prioritaria Lun-Sáb 9am-6pm",
      "Respuesta garantizada en < 24 horas",
      "Proceso acelerado"
    ]
  }
};

// Travel Promotion Banner Component
const TravelPromoBanner = ({ vendedorInfo, userRole }) => {
  // Only show to clients
  if (userRole && userRole !== "client") {
    return null;
  }
  
  // WhatsApp number - same as the main Divídelo support number
  const WHATSAPP_NUMBER = "19548059212";
  const whatsappMessage = encodeURIComponent("¡Hola! Quiero saber más de la oferta a Orlando y Miami 🌴✈️");
  const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMessage}`;
  
  // Travel website - vendor's site or default
  const travelWebsite = vendedorInfo?.travel_website || "www.disneysuperfacil.com";
  // Clean the URL for display (remove https://, http://)
  const displayWebsite = travelWebsite.replace(/^https?:\/\//, '');
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-6 relative overflow-hidden rounded-2xl bg-gradient-to-r from-sky-500 via-blue-600 to-indigo-600"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-40 h-40 bg-white rounded-full -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-60 h-60 bg-white rounded-full translate-x-1/3 translate-y-1/3" />
      </div>
      
      <div className="relative p-5 sm:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          {/* Icon */}
          <div className="flex-shrink-0 w-16 h-16 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center">
            <Plane className="w-8 h-8 text-white" />
          </div>
          
          {/* Content */}
          <div className="flex-1 text-white">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="px-2 py-0.5 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full">
                OFERTA EXCLUSIVA
              </span>
              <span className="text-white/70 text-xs">Solo durante tu proceso de visa</span>
            </div>
            <h3 className="text-lg sm:text-xl font-bold mb-1">
              5 días Orlando + 3 días Miami Beach
            </h3>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-2">
              <p className="text-white/90 text-sm">
                Por solo <span className="text-2xl font-bold text-yellow-300">$199 USD</span>
                <span className="text-white/80 text-xs ml-1">por persona</span>
              </p>
            </div>
            {/* Gift Banner */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-green-400 to-emerald-500 rounded-lg mb-2">
              <span className="text-white text-sm font-bold">🎁 Regalo HOY:</span>
              <span className="text-white text-sm font-semibold">7 días de auto de alquiler</span>
            </div>
            <p className="text-white/80 text-xs">
              Esta reservación fortalece tu solicitud DS-160 demostrando planes de viaje concretos.
              Gracias a nuestra alianza exclusiva.
            </p>
          </div>
          
          {/* CTA - WhatsApp */}
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0"
          >
            <Button 
              className="bg-green-500 hover:bg-green-600 text-white font-semibold px-5 shadow-lg flex items-center gap-2"
              data-testid="travel-promo-btn"
            >
              <MessageCircle className="w-4 h-4" />
              Ver Oferta
            </Button>
          </a>
        </div>
        
        {/* Footer disclaimer */}
        <div className="mt-3 pt-3 border-t border-white/20 flex flex-wrap items-center justify-between gap-2">
          <p className="text-white/50 text-[10px]">
            * No incluye taxes ni seguro de viaje
          </p>
          <p className="text-white/50 text-[10px]">
            {displayWebsite}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

// Earn Money Banner Component - "Gana dinero con Divídelo"
const EarnMoneyBanner = ({ userRole }) => {
  // Only show to clients
  if (userRole && userRole !== "client") {
    return null;
  }
  
  const WHATSAPP_NUMBER = "19548059212";
  const whatsappMessage = encodeURIComponent("¡Hola! Quiero ser un agente de Divídelo y ganar dinero 💰");
  const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMessage}`;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-purple-600 via-indigo-600 to-violet-600"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-40 h-40 bg-white rounded-full translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 left-0 w-60 h-60 bg-white rounded-full -translate-x-1/3 translate-y-1/3" />
      </div>
      
      <div className="relative p-5 sm:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          {/* Icon */}
          <div className="flex-shrink-0 w-16 h-16 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center">
            <DollarSign className="w-8 h-8 text-white" />
          </div>
          
          {/* Content */}
          <div className="flex-1 text-white">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="px-2 py-0.5 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full">
                OPORTUNIDAD
              </span>
              <span className="text-white/70 text-xs">Gana desde tu primer referido</span>
            </div>
            <h3 className="text-lg sm:text-xl font-bold mb-1">
              Gana dinero con Divídelo
            </h3>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-2">
              <p className="text-white/90 text-sm">
                Conviértete en <span className="text-xl font-bold text-yellow-300">Agente</span>
                <span className="text-white/80 text-xs ml-1">y gana comisiones</span>
              </p>
            </div>
            {/* Benefits */}
            <div className="flex flex-wrap gap-2 mb-2">
              <div className="inline-flex items-center gap-1 px-2 py-1 bg-white/10 rounded-lg">
                <TrendingUp className="w-3 h-3 text-green-300" />
                <span className="text-white text-xs">Comisiones atractivas</span>
              </div>
              <div className="inline-flex items-center gap-1 px-2 py-1 bg-white/10 rounded-lg">
                <Users className="w-3 h-3 text-blue-300" />
                <span className="text-white text-xs">Sin límite de referidos</span>
              </div>
            </div>
            <p className="text-white/80 text-xs">
              Ayuda a otros a obtener su visa americana mientras generas ingresos extra.
            </p>
          </div>
          
          {/* CTA - WhatsApp */}
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0"
          >
            <Button 
              className="bg-green-500 hover:bg-green-600 text-white font-semibold px-5 shadow-lg flex items-center gap-2"
              data-testid="earn-money-btn"
            >
              <MessageCircle className="w-4 h-4" />
              Saber más
            </Button>
          </a>
        </div>
        
        {/* Footer */}
        <div className="mt-3 pt-3 border-t border-white/20">
          <p className="text-white/50 text-[10px]">
            * Programa de afiliados disponible para todos los usuarios
          </p>
        </div>
      </div>
    </motion.div>
  );
};

// Upgrade Card Component
const UpgradeCard = ({ planKey, onClick }) => {
  const upgrade = UPGRADE_BENEFITS[planKey];
  const colorStyles = planKey === "vip" 
    ? "from-amber-50 to-orange-50 border-amber-200 hover:border-amber-400"
    : "from-emerald-50 to-teal-50 border-emerald-200 hover:border-emerald-400";
  const iconColor = planKey === "vip" ? "text-amber-600" : "text-emerald-600";
  const Icon = planKey === "vip" ? Crown : Star;
  
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      onClick={onClick}
      className={`p-4 rounded-xl bg-gradient-to-br ${colorStyles} border-2 cursor-pointer transition-all`}
    >
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-5 h-5 ${iconColor}`} />
        <h4 className="font-bold text-navy-900">{upgrade.title}</h4>
      </div>
      <p className="text-xs text-navy-500 mb-3">{upgrade.subtitle}</p>
      <ul className="space-y-1.5">
        {upgrade.benefits.map((benefit, idx) => (
          <li key={idx} className="flex items-start gap-2 text-xs text-navy-700">
            <Check className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${iconColor}`} />
            {benefit}
          </li>
        ))}
      </ul>
      <div className="mt-3 flex items-center justify-end">
        <span className={`text-xs font-medium ${iconColor} flex items-center gap-1`}>
          Conocer más <ArrowRight className="w-3 h-3" />
        </span>
      </div>
    </motion.div>
  );
};

// Main Plan Section Component
const PlanSection = () => {
  const { user, getPlanLevel, getPlanName, isPremium, isVip, hasFeature } = useAuth();
  const navigate = useNavigate();
  
  // Only show to clients
  if (user?.role !== "client") {
    return null;
  }
  
  const planType = user?.plan_type?.toLowerCase() || "estandar";
  const normalizedPlanType = planType === "advanced" ? "premium" : planType;
  const planConfig = PLAN_FEATURES[normalizedPlanType] || PLAN_FEATURES.estandar;
  const Icon = planConfig.icon;
  
  const colorStyles = {
    purple: {
      headerBg: "from-purple-600 to-indigo-700",
      badge: "bg-purple-100 text-purple-700",
      iconBg: "bg-purple-100",
      iconColor: "text-purple-600",
      checkColor: "text-purple-500"
    },
    slate: {
      headerBg: "from-slate-600 to-slate-700",
      badge: "bg-slate-100 text-slate-700",
      iconBg: "bg-slate-100",
      iconColor: "text-slate-600",
      checkColor: "text-slate-500"
    },
    emerald: {
      headerBg: "from-emerald-500 to-teal-600",
      badge: "bg-emerald-100 text-emerald-700",
      iconBg: "bg-emerald-100",
      iconColor: "text-emerald-600",
      checkColor: "text-emerald-500"
    },
    amber: {
      headerBg: "from-amber-500 to-orange-600",
      badge: "bg-amber-100 text-amber-700",
      iconBg: "bg-amber-100",
      iconColor: "text-amber-600",
      checkColor: "text-amber-500"
    }
  };
  
  const style = colorStyles[planConfig.color];
  
  const handleUpgradeClick = () => {
    // Navigate to upgrade page
    navigate("/upgrade");
  };
  
  const handleFeatureClick = (feature) => {
    if (feature.upgradeTo) {
      toast.info(`Esta función está disponible en el plan ${feature.upgradeTo === "vip" ? "VIP Enterprise" : "Premium"}`, {
        description: "Contacta a tu asesor para actualizar"
      });
    } else {
      // Navigate to feature if available
      if (feature.name.includes("Simulador")) navigate("/interview-simulator");
      else if (feature.name.includes("Chat con Asesor")) navigate("/chat");
      else if (feature.name.includes("Optimización")) navigate("/step/optimization");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.95 }}
      className="card-elevated overflow-hidden"
      data-testid="plan-section"
    >
      {/* Header */}
      <div className={`bg-gradient-to-r ${style.headerBg} p-5 text-white`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Icon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-lg">Tu Plan: {planConfig.name}</h3>
              <p className="text-white/80 text-sm">{planConfig.tagline}</p>
            </div>
          </div>
          {isVip() && (
            <div className="px-3 py-1 bg-white/20 rounded-full text-xs font-bold">
              ✨ SERVICIO COMPLETO
            </div>
          )}
        </div>
      </div>
      
      {/* Content */}
      <div className="p-5">
        {/* Included Features */}
        <div className="mb-5">
          <h4 className="text-sm font-semibold text-navy-700 mb-3 flex items-center gap-2">
            <Check className={`w-4 h-4 ${style.checkColor}`} />
            Incluido en tu plan
          </h4>
          <div className="grid sm:grid-cols-2 gap-2">
            {planConfig.included.map((feature, idx) => {
              const FeatureIcon = feature.icon;
              return (
                <div 
                  key={idx}
                  className="flex items-center gap-3 p-3 bg-navy-50 rounded-lg"
                >
                  <div className={`w-8 h-8 rounded-lg ${style.iconBg} flex items-center justify-center`}>
                    <FeatureIcon className={`w-4 h-4 ${style.iconColor}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-navy-800 truncate">{feature.name}</p>
                    <p className="text-xs text-navy-500 truncate">{feature.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Locked Features (if any) */}
        {planConfig.locked.length > 0 && (
          <div className="mb-5">
            <h4 className="text-sm font-semibold text-navy-400 mb-3 flex items-center gap-2">
              <Lock className="w-4 h-4" />
              Disponible en planes superiores
            </h4>
            <div className="grid sm:grid-cols-2 gap-2">
              {planConfig.locked.map((feature, idx) => {
                const FeatureIcon = feature.icon;
                const upgradePlan = feature.upgradeTo === "vip" ? "VIP" : "Premium";
                return (
                  <div 
                    key={idx}
                    onClick={() => handleFeatureClick(feature)}
                    className="flex items-center gap-3 p-3 bg-navy-50/50 rounded-lg opacity-60 cursor-pointer hover:opacity-80 transition-opacity"
                  >
                    <div className="w-8 h-8 rounded-lg bg-navy-100 flex items-center justify-center relative">
                      <FeatureIcon className="w-4 h-4 text-navy-400" />
                      <Lock className="w-3 h-3 text-navy-400 absolute -bottom-1 -right-1" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-navy-600 truncate">{feature.name}</p>
                      <p className="text-xs text-navy-400">Disponible en {upgradePlan}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Upgrade Options */}
        {!isVip() && (
          <div className="border-t border-navy-100 pt-5">
            <h4 className="text-sm font-semibold text-navy-700 mb-3">
              Mejora tu experiencia
            </h4>
            <div className={`grid ${normalizedPlanType === "estandar" ? "sm:grid-cols-2" : ""} gap-3`}>
              {normalizedPlanType === "estandar" && (
                <UpgradeCard planKey="premium" onClick={handleUpgradeClick} />
              )}
              <UpgradeCard planKey="vip" onClick={handleUpgradeClick} />
            </div>
          </div>
        )}
        
        {/* VIP Badge for VIP users */}
        {isVip() && (
          <div className="border-t border-navy-100 pt-4 mt-4">
            <div className="flex items-center justify-center gap-2 text-amber-600">
              <Crown className="w-5 h-5" />
              <span className="font-semibold">Tienes acceso a todas las funciones</span>
            </div>
          </div>
        )}
      </div>
      
      {/* Travel Promotion Banner - Always visible */}
      <div className="px-5 pb-5">
        <TravelPromoBanner vendedorInfo={user?.vendedor_info} />
      </div>
    </motion.div>
  );
};

export default PlanSection;
export { TravelPromoBanner, EarnMoneyBanner };
